import React, { useState, useEffect } from 'react';
import themeFrameMappingApi from '@services/themeFrameMappingApi.jsx';

const ThemeFrameSelectionModal = ({ 
    themeId, 
    availableFrames = [], 
    onClose, 
    onSave, 
    isEditMode = false, 
    initialData = null 
}) => {
    const VITE_ADMIN_BACKEND_API_URL = import.meta.env.VITE_ADMIN_BACKEND_API_URL;
    // 상태 관리
    const [selectedFrameId, setSelectedFrameId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedFrameInfo, setSelectedFrameInfo] = useState(null);

    // 초기 데이터 설정 (수정 모드)
    useEffect(() => {
        if (isEditMode && initialData) {
            console.log('수정 모드 초기 데이터:', initialData);
            
            setSelectedFrameId(initialData.photoFrameId);
            
            // 수정 모드에서는 현재 선택된 프레임 정보를 직접 사용
            if (initialData.photoFrame) {
                setSelectedFrameInfo({
                    id: initialData.photoFrame.id,
                    frameName: initialData.photoFrame.frameName,
                    isActive: initialData.photoFrame.isActive,
                    frameDataResourceId: initialData.photoFrame.frameDataResourceId
                });
            } else {
                // availableFrames에서 찾기 시도
                const frameInfo = availableFrames.find(frame => frame.id === initialData.photoFrameId);
                if (frameInfo) {
                    setSelectedFrameInfo(frameInfo);
                }
            }
        }
    }, [isEditMode, initialData, availableFrames]);

    // 디버깅을 위한 상태 추적
    useEffect(() => {
        console.log('ThemeFrameSelectionModal - 현재 상태:', {
            selectedFrameId,
            selectedFrameInfo,
            availableFrames: availableFrames.map(frame => ({
                id: frame.id,
                frameName: frame.frameName,
                frameDataResourceId: frame.frameDataResourceId,
                프레임명있는지: frame.frameName ? 'YES' : 'NO',
                이미지있는지: frame.frameDataResourceId ? 'YES' : 'NO'
            })),
            availableFramesLength: availableFrames.length
        });
    }, [selectedFrameId, selectedFrameInfo, availableFrames]);

    // 프레임 선택 핸들러
    const handleFrameSelect = (frameId) => {
        setSelectedFrameId(frameId);
        const frameInfo = availableFrames.find(frame => frame.id === frameId);
        setSelectedFrameInfo(frameInfo);
    };



    // 저장 핸들러
    const handleSave = async () => {
        if (!selectedFrameId) {
            alert('프레임을 선택해주세요.');
            return;
        }

        setIsLoading(true);
        try {
            let result;
            
            if (isEditMode) {
                // 수정 모드
                result = await themeFrameMappingApi.updateThemeFrameMapping(
                    initialData.mappingId, 
                    {}
                );
            } else {
                // 추가 모드
                result = await themeFrameMappingApi.addFrameToTheme(
                    themeId,
                    selectedFrameId,
                    {}
                );
            }

            onSave(result);
            onClose();
        } catch (error) {
            console.error('저장 실패:', error);
            alert(`저장 중 오류가 발생했습니다: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <div className="modal-overlay active">
                <div className="modal-container theme-frame-modal">
                    <div className="modal-header">
                        <h3 className="modal-title">
                            테마 프레임 {isEditMode ? '수정' : '선택'}
                        </h3>
                        <button className="modal-close" onClick={onClose}>
                            <i className="ri-close-line"></i>
                        </button>
                    </div>
                    
                    <div className="modal-body">
                        <div className="modal-content">
                            {/* 프레임 선택 섹션 (추가 모드에서만) */}
                            {!isEditMode && (
                                <div className="modal-form-group">
                                    <label className="modal-label">프레임 선택</label>
                                    {availableFrames.length === 0 ? (
                                        <div className="no-frames-message">
                                            사용 가능한 프레임이 없습니다.
                                        </div>
                                    ) : (
                                        <div className="frame-selection-grid">
                                            {availableFrames.map((frame) => {
                                                console.log('각 프레임 상세 데이터:', {
                                                    id: frame.id,
                                                    frameName: frame.frameName,
                                                    frameDataResourceId: frame.frameDataResourceId,
                                                    frameType: frame.frameType,
                                                    orientation: frame.orientation,
                                                    전체객체: frame
                                                });
                                                return (
                                                    <div 
                                                        key={frame.id}
                                                        className={`frame-selection-item ${selectedFrameId === frame.id ? 'selected' : ''}`}
                                                        onClick={() => handleFrameSelect(frame.id)}
                                                    >
                                                        <div className="frame-info">
                                                            <h4>{frame.frameName || '프레임명 없음'}</h4>
                                                            <div className="frame-preview">
                                                                {frame.frameDataResourceId ? (
                                                                    <img 
                                                                        src={`${VITE_ADMIN_BACKEND_API_URL}/resource/view/${frame.frameDataResourceId}`} 
                                                                        alt={frame.frameName || '프레임 이미지'}
                                                                        onError={(e) => {
                                                                            console.log('이미지 로드 실패:', e.target.src);
                                                                            e.target.src = '/images/frameData/frame1.png';
                                                                        }}
                                                                        onLoad={() => {
                                                                            console.log('이미지 로드 성공:', frame.frameName);
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <div className="no-image-placeholder">
                                                                        <i className="ri-image-line"></i>
                                                                        <span>이미지 없음</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* 선택된 프레임 정보 표시 */}
                            {selectedFrameInfo && (
                                <div className="modal-form-group">
                                    <label className="modal-label">선택된 프레임 정보</label>
                                    <div className="selected-frame-info">
                                        <div className="frame-details">
                                            <h4>{selectedFrameInfo.frameName}</h4>
                                        </div>
                                    </div>
                                </div>
                            )}


                        </div>
                    </div>
                    
                    <div className="modal-footer">
                        <button 
                            className="modal-btn modal-btn-cancel" 
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            취소
                        </button>
                        <button 
                            className="modal-btn modal-btn-primary" 
                            onClick={handleSave}
                            disabled={isLoading || (!isEditMode && !selectedFrameId)}
                        >
                            {isLoading ? '저장 중...' : (isEditMode ? '수정' : '추가')}
                        </button>
                    </div>
                </div>
            </div>

            <style>{`
                .theme-frame-modal {
                    max-width: 800px;
                    width: 90%;
                    animation: none !important;
                    transform: none !important;
                }

                .frame-selection-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
                    gap: 15px;
                    margin-top: 10px;
                }

                .frame-selection-item {
                    border: 2px solid #e0e0e0;
                    border-radius: 8px;
                    padding: 15px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    background: #fff;
                    min-height: 200px;
                    display: flex;
                    flex-direction: column;
                }

                .frame-selection-item:hover {
                    border-color: #007bff;
                    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.2);
                }

                .frame-selection-item.selected {
                    border-color: #007bff;
                    background: #f8f9ff;
                    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
                }

                .frame-info {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                }

                .frame-info h4 {
                    margin: 0 0 10px 0;
                    font-size: 16px;
                    font-weight: 600;
                    color: #333;
                    text-align: center;
                }

                .frame-preview {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .frame-preview img {
                    max-width: 100%;
                    max-height: 120px;
                    object-fit: contain;
                    border-radius: 4px;
                    background-color: #f5f5f5;
                }

                .selected-frame-info {
                    background: #f8f9fa;
                    border: 1px solid #dee2e6;
                    border-radius: 6px;
                    padding: 15px;
                }

                .frame-details h4 {
                    margin: 0;
                    font-size: 18px;
                    color: #333;
                }

                .no-frames-message {
                    text-align: center;
                    padding: 40px 20px;
                    color: #666;
                    font-size: 16px;
                    background: #f8f9fa;
                    border: 1px solid #dee2e6;
                    border-radius: 6px;
                    margin-top: 10px;
                }

                .no-image-placeholder {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    height: 120px;
                    color: #999;
                    background: #f8f9fa;
                    border: 2px dashed #ddd;
                    border-radius: 4px;
                }

                .no-image-placeholder i {
                    font-size: 24px;
                    margin-bottom: 5px;
                }

                .no-image-placeholder span {
                    font-size: 12px;
                }
            `}</style>
        </>
    );
};

export default ThemeFrameSelectionModal;